import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Tag, Typography, Alert, Tooltip, Progress, message, Space, Switch, Select, Modal, Button } from 'antd';
import {
    ClockCircleOutlined,
    PlayCircleOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    SyncOutlined, // Added from new code
    FieldTimeOutlined, // Added from new code
    SecurityScanOutlined, // Added from new code
} from '@ant-design/icons';
import Pusher from "pusher-js"; // Added from new code
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import useSound from 'use-sound';
import alertSoundFile from '../../assets/alarm.wav';

const { Title, Text } = Typography; // Added Text from new code
const { Option } = Select;

const formatTime = (datetime) => {
    if (!datetime) return null;
    const d = new Date(datetime);
    return d.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const ActiveRounds = () => {
    const [activeRounds, setActiveRounds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { user } = useAuth();

    // Audio Alert States
    const [soundEnabled, setSoundEnabled] = useState(false);
    const [alertFrequency, setAlertFrequency] = useState(5); // in minutes
    const [slowScanThreshold, setSlowScanThreshold] = useState(15); // in minutes
    const [silencedCompanies, setSilencedCompanies] = useState([]);
    const [isAlertModalVisible, setIsAlertModalVisible] = useState(false); // New state for popup
    const lastAlertTimeRef = useRef(0);
    const previousCriticallyOverdueCount = useRef(0);

    const [playAlert, { stop: stopAlert }] = useSound(alertSoundFile, { volume: 0.8, loop: true }); // Make it loop until acknowledged
    const [statData, setStatData] = useState({ // Added from new code
        total: 0,
        completed_checkpoints: 0,
        remaining_checkpoints: 0
    });

    const fetchActiveRounds = async () => {
        try {
            setLoading(true);
            const res = await api.get('/superadmin/active-rounds');
            if (res.data.success) {
                setActiveRounds(res.data.data);
                setError('');

                // Update overall stats based on fetched data (from new code)
                let totalCp = 0;
                let scannedCp = 0;

                res.data.data.forEach(round => {
                    totalCp += round.total_checkpoints || 0;
                    scannedCp += round.scanned_checkpoints || 0;
                });

                setStatData({
                    total: res.data.data.length,
                    completed_checkpoints: scannedCp,
                    remaining_checkpoints: totalCp - scannedCp
                });

            }
        } catch (err) {
            console.error('Failed to fetch active rounds:', err);
            setError('ไม่สามารถโหลดรอบตรวจปัจจุบันได้');
            message.error("ไม่สามารถโหลดข้อมูลรอบการทำงานปัจจุบันได้"); // Added from new code
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let interval;
        let pusher = null;
        let channel = null;

        if (['super_admin', 'multi_company_patrol', 'multi_company_admin'].includes(user?.role)) {
            fetchActiveRounds();
            interval = setInterval(fetchActiveRounds, 60000); // Re-fetch every 1 minute

            // Pusher Real-time setup
            try {
                const token = localStorage.getItem('auth_token');
                const pusherKey = process.env.REACT_APP_PUSHER_KEY || 'f88e5d7c0ab9a2b5b7ac';
                const pusherCluster = process.env.REACT_APP_PUSHER_CLUSTER || 'ap1';

                if (token && pusherKey) {
                    // Enable pusher logging - don't include in production
                    Pusher.logToConsole = true;

                    pusher = new Pusher(pusherKey, {
                        cluster: pusherCluster,
                        authEndpoint: `${process.env.REACT_APP_API_URL}/auth/pusher/auth`,
                        auth: {
                            headers: {
                                Authorization: `Bearer ${token}`
                            }
                        }
                    });

                    channel = pusher.subscribe('private-superadmin');

                    pusher.connection.bind('connected', () => {
                        console.log('Pusher connection established successfully');
                    });

                    pusher.connection.bind('error', (err) => {
                        console.error('Pusher connection error:', err);
                    });

                    channel.bind('active-round-update', (data) => {
                        console.log('Received active-round-update from Pusher:', data);
                        fetchActiveRounds(); // Refresh dashboard on new check-in
                    });

                    channel.bind('pusher:subscription_succeeded', () => {
                        console.log('Pusher subscribed to private-superadmin successfully!');
                    });

                    channel.bind('pusher:subscription_error', (error) => {
                        console.error('Pusher subscription error:', error);
                    });
                } else {
                    console.log('Pusher setup skipped: Missing token or pusherKey');
                }
            } catch (error) {
                console.error('Error initializing Pusher:', error);
            }
        }

        return () => {
            if (interval) clearInterval(interval);
            if (channel) {
                channel.unbind_all();
                if (pusher) pusher.unsubscribe('private-superadmin');
            }
            if (pusher) {
                pusher.disconnect();
            }
        };
    }, [user?.role]);

    // Determine if this round should have started for more than 30 mins, has no scans, and company not silenced
    const isCriticallyOverdue = (record) => {
        if (record.status !== 'กำลังดำเนินการ') return false;
        if (record.first_scan) return false;
        if (silencedCompanies.includes(record.company_id)) return false;

        const now = new Date();
        const [startHour, startMinute] = record.start_time.split(':').map(Number);

        // Handle cross-midnight by comparing correctly
        const startTimeDate = new Date();
        startTimeDate.setHours(startHour, startMinute, 0, 0);

        // If start time is greater than now, it might be from yesterday (e.g. started at 23:00, now is 01:00)
        if (startTimeDate > now) {
            startTimeDate.setDate(startTimeDate.getDate() - 1);
        }

        const diffMinutes = (now - startTimeDate) / (1000 * 60);
        return diffMinutes >= slowScanThreshold;
    };

    // Determine if it's just a normal no scan alert (not necessarily 30 mins) for UI styling
    const isAlertNoScan = (record) => {
        if (record.status !== 'กำลังดำเนินการ') return false;
        return !record.first_scan;
    };

    const columns = [
        {
            title: 'บริษัท',
            dataIndex: 'company_name',
            key: 'company_name',
            width: 160,
        },
        {
            title: 'ชื่อรอบตรวจ',
            dataIndex: 'round_name',
            key: 'round_name',
            width: 160,
        },
        {
            title: 'เวลา',
            key: 'time',
            width: 130,
            render: (_, record) => (
                <span>{record.start_time.substring(0, 5)} - {record.end_time.substring(0, 5)}</span>
            )
        },
        {
            title: 'สถานะ',
            dataIndex: 'status',
            key: 'status',
            width: 160,
            render: (status, record) => {
                let color = 'blue';
                let icon = <ClockCircleOutlined />;
                if (status === 'กำลังดำเนินการ') {
                    color = 'green';
                    icon = <PlayCircleOutlined spin />;
                } else if (status === 'กำลังจะจบ') {
                    color = 'orange';
                }
                return (
                    <span>
                        <Tag color={color} icon={icon}>{status}</Tag>
                        {isAlertNoScan(record) && (
                            <Tooltip title="รอบนี้เริ่มแล้วแต่ยังไม่มีการสแกน!">
                                <Tag color="red" icon={<WarningOutlined />} style={{ marginTop: 4 }}>
                                    ยังไม่เริ่มสแกน!
                                </Tag>
                            </Tooltip>
                        )}
                    </span>
                );
            }
        },
        {
            title: 'สแกนแรก',
            key: 'first_scan',
            width: 120,
            render: (_, record) => {
                if (!record.first_scan) {
                    if (record.status === 'กำลังดำเนินการ') {
                        return <span style={{ color: '#ff4d4f', fontWeight: 600 }}>ยังไม่มีการสแกน</span>;
                    }
                    return <span style={{ color: '#aaa' }}>-</span>;
                }
                return (
                    <span style={{ color: '#52c41a', fontWeight: 500 }}>
                        <CheckCircleOutlined style={{ marginRight: 4 }} />
                        {formatTime(record.first_scan)}
                    </span>
                );
            }
        },
        {
            title: 'สแกนล่าสุด',
            key: 'last_scan',
            width: 100,
            render: (_, record) => {
                if (!record.last_scan) return <span style={{ color: '#aaa' }}>-</span>;
                return <span>{formatTime(record.last_scan)}</span>;
            }
        },
        {
            title: 'จุดตรวจ',
            key: 'checkpoints',
            width: 170,
            render: (_, record) => {
                const total = parseInt(record.total_checkpoints) || 0;
                const scanned = parseInt(record.scanned_checkpoints) || 0;
                if (total === 0) return <span style={{ color: '#aaa' }}>ไม่มี checkpoint</span>;
                const percent = Math.round((scanned / total) * 100);
                const color = percent === 100 ? '#52c41a' : percent > 0 ? '#1890ff' : '#ff4d4f';
                return (
                    <Tooltip title={`สแกนแล้ว ${scanned} จาก ${total} จุด`}>
                        <div style={{ minWidth: 120 }}>
                            <Progress
                                percent={percent}
                                size="small"
                                strokeColor={color}
                                format={() => `${scanned}/${total}`}
                            />
                        </div >
                    </Tooltip >
                );
            }
        },
    ];

    const sortedActiveRounds = [...activeRounds].sort((a, b) => {
        // 1. Prioritize critically overdue ones
        const aCrit = isCriticallyOverdue(a);
        const bCrit = isCriticallyOverdue(b);
        if (aCrit && !bCrit) return -1;
        if (!aCrit && bCrit) return 1;

        // 2. Prioritize ones that have not started scanning (Red alert)
        const aAlert = isAlertNoScan(a);
        const bAlert = isAlertNoScan(b);

        if (aAlert && !bAlert) return -1;
        if (!aAlert && bAlert) return 1;

        // 3. Sort by start_time ascending
        if (a.start_time && b.start_time && a.start_time !== b.start_time) {
            return a.start_time.localeCompare(b.start_time);
        }

        return 0;
    });

    const criticallyOverdueRounds = activeRounds.filter(isCriticallyOverdue);
    const alertCount = criticallyOverdueRounds.length;
    const normalAlertCount = activeRounds.filter(isAlertNoScan).length; // Just for standard UI if needed

    // Audio Playback Effect
    useEffect(() => {
        let checkInterval;

        const checkAndPlayAlert = () => {
            if (isAlertModalVisible) return; // ไม่เล่นซ้อนถ้า popup ยังเปิดค้างอยู่

            if (alertCount > 0 && soundEnabled) {
                const now = Date.now();
                const cooldownMs = alertFrequency * 60 * 1000;

                const timePassed = now - lastAlertTimeRef.current >= cooldownMs;
                const newOverdueAppeared = alertCount > previousCriticallyOverdueCount.current;

                if (timePassed || newOverdueAppeared) {
                    console.log('Playing alert sound for', alertCount, 'overdue rounds');
                    playAlert();
                    setIsAlertModalVisible(true);
                    lastAlertTimeRef.current = now;
                }
            }
            previousCriticallyOverdueCount.current = alertCount;
        };

        // 1. Check immediately when dependencies change
        checkAndPlayAlert();

        // 2. Set an interval to keep checking every 10 seconds 
        //    (so if time passes without Data/Count changing, it still rings)
        if (soundEnabled && alertCount > 0) {
            checkInterval = setInterval(() => {
                checkAndPlayAlert();
            }, 10000); // Check every 10 seconds
        }

        if (alertCount === 0) {
            lastAlertTimeRef.current = 0; // Reset cooldown if everything resolved
            if (isAlertModalVisible) {
                setIsAlertModalVisible(false);
                if (stopAlert) stopAlert();
            }
        }

        return () => {
            if (checkInterval) clearInterval(checkInterval);
        };
    }, [alertCount, soundEnabled, alertFrequency, playAlert, stopAlert, isAlertModalVisible]);

    const handleAcknowledgeAlert = () => {
        setIsAlertModalVisible(false);
        if (stopAlert) stopAlert();
        // อัปเดตเวลาล่าสุดตอนที่กดปิด Popup เพื่อรอให้ครบเวลาจริงๆ นับจากการกด
        lastAlertTimeRef.current = Date.now();
    };

    const handleSoundToggle = (checked) => {
        setSoundEnabled(checked);
        if (checked) {
            // เมื่อเปิดเสียง ให้เริ่มนับเวลา Cooldown ใหม่ (ป้องกันการดังทันทีกดเปิด)
            lastAlertTimeRef.current = Date.now();
        } else {
            // เมื่อปิดเสียง ให้หยุดเสียงและปิด popup ถ้ามีอยู่
            setIsAlertModalVisible(false);
            if (stopAlert) stopAlert();
        }
    };

    if (!['super_admin', 'multi_company_patrol', 'multi_company_admin'].includes(user?.role)) {
        return <Alert message="ไม่มีสิทธิ์เข้าถึง" type="error" />;
    }

    return (
        <div>
            <div className="page-header" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Title level={2}>ตรวจสอบรอบตรวจปัจจุบัน</Title>
                    <p>ติดตามรอบตรวจที่กำลังดำเนินการอยู่ของทุกบริษัทแบบเรียลไทม์ (±15 นาที) อัปเดตอัตโนมัติเมื่อมีการสแกน</p>
                </div>

                <Card size="small" style={{ background: soundEnabled ? '#e6f7ff' : '#f5f5f5', borderColor: soundEnabled ? '#91d5ff' : '#d9d9d9' }}>
                    <Space direction="vertical" size="small">
                        <Space>
                            <span>🔊 เสียงเตือนรอบสแกนช้า</span>
                            <Switch checked={soundEnabled} onChange={handleSoundToggle} checkedChildren="เปิด" unCheckedChildren="ปิด" />
                        </Space>
                        <Space>
                            <span style={{ fontSize: 12, color: '#666' }}>เตือนเมื่อช้าเกิน:</span>
                            <Select size="small" value={slowScanThreshold} onChange={setSlowScanThreshold} style={{ width: 90 }}>
                                <Option value={10}>10 นาที</Option>
                                <Option value={15}>15 นาที</Option>
                                <Option value={30}>30 นาที</Option>
                                <Option value={45}>45 นาที</Option>
                            </Select>
                        </Space>
                        {soundEnabled && (
                            <Space>
                                <span style={{ fontSize: 12, color: '#666' }}>เตือนซ้ำทุกๆ:</span>
                                <Select size="small" value={alertFrequency} onChange={setAlertFrequency} style={{ width: 100 }}>
                                    <Option value={5}>5 นาที</Option>
                                    <Option value={10}>10 นาที</Option>
                                    <Option value={15}>15 นาที</Option>
                                </Select>
                            </Space>
                        )}
                    </Space>
                </Card>
            </div>

            {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

            {alertCount > 0 && (
                <Alert
                    message={`⚠️ พบ ${alertCount} รอบตรวจที่ล่าช้าเกิน ${slowScanThreshold} นาที และยังไม่มีการสแกน!`}
                    type="error"
                    showIcon
                    style={{ marginBottom: 16, fontWeight: 600 }}
                />
            )}

            <Card>
                <Table
                    dataSource={sortedActiveRounds}
                    columns={columns}
                    rowKey="round_id"
                    loading={loading}
                    pagination={{ pageSize: 15 }}
                    locale={{ emptyText: 'ไม่มีรอบตรวจในขณะนี้' }}
                    rowClassName={(record) => {
                        if (isCriticallyOverdue(record)) return 'ant-table-row-critical-alert';
                        if (isAlertNoScan(record)) return 'ant-table-row-alert';
                        return '';
                    }}
                />
            </Card>

            <Modal
                title={
                    <Space>
                        <WarningOutlined style={{ color: '#ff4d4f', fontSize: '24px' }} />
                        <span style={{ fontSize: '18px', color: '#ff4d4f' }}>แจ้งเตือนรอบตรวจล่าช้า!</span>
                    </Space>
                }
                open={isAlertModalVisible}
                onOk={handleAcknowledgeAlert}
                onCancel={handleAcknowledgeAlert}
                footer={[
                    <Button key="submit" type="primary" danger size="large" onClick={handleAcknowledgeAlert}>
                        รับทราบ ปิดเสียงชั่วคราว
                    </Button>,
                ]}
                centered
                closable={false}
                maskClosable={false}
            >
                <div style={{ padding: '20px 0', textAlign: 'center' }}>
                    <Title level={4}>พบ {alertCount} รอบตรวจที่ล่าช้าเกิน {slowScanThreshold} นาที</Title>
                    <p style={{ fontSize: '16px', color: '#666' }}>
                        กรุณาตรวจสอบตารางด้านล่างและติดตามการทำงานของเจ้าหน้าที่
                    </p>
                    <p style={{ fontSize: '14px', color: '#888', marginTop: '16px' }}>
                        *ระบบจะแจ้งเตือนซ้ำใน {alertFrequency} นาที หากยังไม่มีการสแกนหรือแก้ไข
                    </p>
                </div>
            </Modal>

            <style>{`
                .ant-table-row-alert td {
                    background-color: #fff2f0 !important;
                }
            `}</style>
        </div>
    );
};

export default ActiveRounds;
